import { isAxiosError } from 'axios'

import { saveWorksBeforeAddToCart } from './workSaving'
import { upsertWorkToCart } from './upsertWorkToCart'
import { trackProjectsAddToCart } from './trackProjectsAddToCart'
import { navigateToNextStep } from './navigation'
import type { AddToCartDependencies } from './types'

/**
 * Core add to cart business logic
 * Orchestrates the add to cart flow with injected dependencies
 *
 * @param deps - All dependencies required for add to cart operation
 * @param setIsUpdatingCart - State setter for loading indicator
 */
export async function executeAddToCart(deps: AddToCartDependencies, setIsUpdatingCart: (updating: boolean) => void): Promise<void> {
  setIsUpdatingCart(true)

  let workIdsToSave
  try {
    // Save work quantities if needed
    if (deps.projects.length > 0) {
      await saveWorksBeforeAddToCart(deps.cartTenant, deps.projects, deps.worksData, deps.isCareAgent, deps.authorizationHeader, deps.logger)
    }

    // Add items to cart
    workIdsToSave = deps.workIdsWithValidQuantities
    await upsertWorkToCart(workIdsToSave, deps.culture, deps.upsertCartAsync, deps.upsertCartMultiWorkAsync, deps.authorizationHeader, deps.logger)
  } catch (err) {
    let shouldContinue = false

    // For network errors, attempt to verify if items were actually added to cart
    if (isAxiosError(err) && err.code === 'ERR_NETWORK' && !err.response) {
      deps.logger.warn('Cart request returned network error, attempting to verify cart contents', {
        workIds: deps.workIdsWithValidQuantities,
        culture: deps.culture,
        errorName: err.name,
        errorMessage: err.message,
        errorCode: err.code,
        errorStack: err.stack,
      })

      const workIdsAreInCart = await deps.verifyCartContainsWorkIds(deps.workIdsWithValidQuantities, deps.culture, deps.authorizationHeader, deps.logger)

      if (workIdsAreInCart) {
        deps.logger.info('Cart verification succeeded - items found in cart, continuing with flow', {
          workIds: deps.workIdsWithValidQuantities,
        })
        shouldContinue = true
      } else {
        deps.logger.warn('Cart verification failed - items not found in cart after network error', {
          workIds: deps.workIdsWithValidQuantities,
          culture: deps.culture,
        })
      }
    }

    // If verification didn't pass or this is a different error, show error and stop
    if (!shouldContinue) {
      setIsUpdatingCart(false)
      const errorMessage = deps.projects.length > 1 ? deps.t('alert-messages.unable-to-add-products-to-cart') : deps.t('alert-messages.unable-to-add-to-cart')
      deps.setError(errorMessage)
      deps.logger.warn(`Error adding to cart: ${deps.workIdsWithValidQuantities.join(', ')}`, { error: err })
      return
    }
  }

  // Track the add to cart event
  trackProjectsAddToCart(deps.projects, deps.worksData, deps.trackAddToCart)

  // Handle CARE agent flow - show success message and don't navigate
  if (deps.isCareAgent) {
    setIsUpdatingCart(false)
    deps.setSuccess(deps.t('alert-messages.customer-cart-updated'))
    return
  }

  // Navigate to next step
  navigateToNextStep(deps.projects, deps.nextStepUrl, deps.logger)
}
